import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { getFramework, useActiveStageIndex, useIdentity, useScale } from '@/framework';
import { SMART_GUIDE_EVENT_NAMES, type IBlockInfo, type ILayerInfo, type ILayerProps } from '@/version-types';
import './helper-view.less';

type Props = {
    index?: number;
};
const HelperView: React.FC<Props> = ({ index = 0 }) => {
    // 吸附线
    const [adsorptionInfo, setAdsorptionInfo] = useState<ILayerInfo[]>([]);
    // 等间距线
    const [equidistantInfo, setEquidistantInfo] = useState<IBlockInfo[]>([]);

    const identity = useIdentity();
    const framework = getFramework(identity);
    const scale = useScale();

    // console.log('smart-guides 2', layer);

    const activeStageIndex = useActiveStageIndex();
    const isActiveStage = activeStageIndex === index;

    useEffect(() => {
        const h1 = framework.events.on(SMART_GUIDE_EVENT_NAMES.SET_LINES, setAdsorptionInfo);
        const h2 = framework.events.on(SMART_GUIDE_EVENT_NAMES.SET_BLOCKS, setEquidistantInfo);
        return () => {
            framework.events.off(h1);
            framework.events.off(h2);
        };
    }, []);

    if ((!adsorptionInfo.length && !equidistantInfo.length) || !isActiveStage) return null;

    const referredLayerMap = [...adsorptionInfo, ...equidistantInfo].reduce((layerMap, item) => {
        if (item.id) {
            const layer = framework.getLayer(item.id);
            if (layer) {
                layerMap.set(item.id, layer);
            }
        }
        return layerMap;
    }, new Map<string, ILayerProps>());

    return (
        <div className="l-smart-guides">
            <div>
                {/* 高亮被对齐的图层 */}
                {Array.from(referredLayerMap.values()).map((referredLayer) => {
                    return (
                        <div
                            className="l-smart-referred-layer"
                            key={referredLayer.id}
                            style={{
                                left: (referredLayer.left - 1) * scale,
                                top: (referredLayer.top - 1) * scale,
                                width: (referredLayer.width + 2) * scale,
                                height: (referredLayer.height + 2) * scale,
                                transform: referredLayer.rotate ? `rotate(${referredLayer.rotate}deg)` : '',
                            }}
                        ></div>
                    );
                })}

                {/* 吸附线 */}
                {adsorptionInfo.map((item, idx1) => {
                    const { points, isHorizontal } = item;
                    const [startPoint] = points;
                    const [endPoint] = points.slice(-1);
                    return (
                        <React.Fragment key={idx1}>
                            <div
                                className={classnames('l-smart-guide-line', {
                                    'l-smart-guide-line__horizontal': isHorizontal,
                                    'l-smart-guide-line__vertical': !isHorizontal,
                                })}
                                style={{
                                    left: startPoint.x * scale,
                                    top: startPoint.y * scale,
                                    width: isHorizontal ? (endPoint.x - startPoint.x) * scale : 1,
                                    height: isHorizontal ? 1 : (endPoint.y - startPoint.y) * scale,
                                }}
                            ></div>

                            {points.map((point, idx2) => {
                                return (
                                    <div
                                        key={idx1 + '-' + idx2}
                                        className="l-smart-guide-line__point"
                                        style={{
                                            left: point.x * scale,
                                            top: point.y * scale,
                                        }}
                                    ></div>
                                );
                            })}
                        </React.Fragment>
                    );
                })}

                {/* 等间距线 */}
                {equidistantInfo.map((item, idx3) => {
                    const blockStyle = {
                        left: item.left * scale,
                        top: item.top * scale,
                        width: item.width * scale,
                        height: item.height * scale,
                    };

                    return (
                        <div
                            key={idx3}
                            className={classnames('l-smart-interval', {
                                'l-smart-interval__horizontal': item.isHorizontal,
                                'l-smart-interval__vertical': !item.isHorizontal,
                            })}
                            style={blockStyle}
                        >
                            <div className="l-smart-interval__gap-line"></div>
                            <div className="l-smart-interval__point l-smart-interval__point1"></div>
                            <div className="l-smart-interval__point l-smart-interval__point2"></div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(HelperView);
